import { Box } from "@mui/material";
import { PrimaryNav } from "@teamworksdev/nav";
import { useEnvironmentType } from "apps/web/src/contexts/useEnvType";
import { useReportingUserIdentify } from "apps/web/src/utils/reporting/useReportingUserIdentiy";
import { AMSDashboardMFE } from "apps/web/src/views/AMSDashboardMFE";
import { useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom-v5-compat";
import { NutritionWhiteIcon } from "shared-ui/global/Icons";
import { validate } from "uuid";
import { AgreementFormModal } from "../../../components/AgreementForm/AgreementFormModal";
import { useSnackbar } from "../../../components/Snackbar/SnackbarContext";
import { useNutritionFlags } from "../../../hooks/useNutritionFlags";
import { useApolloClient, type ApolloClient } from "@apollo/client";
import {
  AgreementFormFragment,
  FetchAxleAppSwitcherJwtOrErrorDocument,
  FetchAxleAppSwitcherJwtOrErrorQuery,
  useAddAgreementFormSubmissionMutation,
  useMyFeatureFlagsQuery,
  useMyUserQuery,
  usePublishedNotificationSubscription,
  useUnsubmittedAgreementFormForUserQuery,
} from "../../../types";
import { useLogout, useUser } from "../../../utils/tokens";
import { DevRouter } from "../Dev/DevRouter";
import { InactivityWarning } from "../InactivityWarning";
import { getFeatureFlagsDetails, getOrgUserDetails } from "../OrgUtils";
import {
  DEFAULT_ORG_ID,
  DEFAULT_USER_NAME,
  OrgAndStaffRouterProps,
  getOrgPrimaryNavLinks,
  helperMenuLinks,
  tacticalHelperMenuLinks,
  orgAdditionalSettingsLinks,
  getOrgBottomLinks,
  parseAxleAppAccess,
} from "../PrimaryNavUtils";
import { AuthRoute } from "../ProtectedRoutes";
import { addTimelineMealNotificationToCache } from "../TimelineNotificationUtils";
import { AthleteLegacyRouter } from "./Athlete/AthleteLegacyRouter";
import { DataRouter } from "./Data/DataRouter";
import { EducationResourcesRouter } from "./EducationResources/EducationResourcesRouter";
import { RecipeRouter } from "./Food/RecipeRouter";
import { FoodManagementRouter } from "./FoodManagement/FoodManagementRouter";
import { KitchenRouter } from "./Kitchen/KitchenRouter";
import { MenuItemRouter } from "./Kitchen/MenuItemRouter";
import { MealPlansRouter } from "./MealPlans/MealPlansRouter";
import { MenuAttendanceRouter } from "./MenuAttendance/MenuAttendanceRouter";
import { MenuBuilderRouter } from "./MenuBuilder/MenuBuilderRouter";
import { MessagePage } from "./Message/MessagePage";
import { UnityMessagingMFE } from "./Message/Unity/UnityMessageMFE";
import { SettingsRouter } from "./Settings/SettingsRouter";
import { TeamRouter } from "./Team/TeamRouter";
import { TeamManagementRouter } from "./TeamManagement/TeamManagementRouter";
import { ViewAttendanceRouter } from "./ViewAttendance/ViewAttendanceRouter";

const APP_SWITCHER_ERROR_MESSAGE = "Unable to switch apps right now. Please try again.";

async function getAppSwitcherToken(client: ApolloClient<object>): Promise<{ token: string | undefined; errorMessage: string }> {
  try {
    const { data } = await client.query<FetchAxleAppSwitcherJwtOrErrorQuery>({
      query: FetchAxleAppSwitcherJwtOrErrorDocument,
      fetchPolicy: "no-cache",
    });
    const { token, error } = data?.fetchAxleAppSwitcherJWTOrError ?? {};
    if (token) {
      return { token, errorMessage: "" };
    }
    console.error("Error getting app switcher token", error);
    return { token: undefined, errorMessage: APP_SWITCHER_ERROR_MESSAGE };
  } catch (err) {
    console.error("Error getting app switcher token", err);
    return { token: undefined, errorMessage: APP_SWITCHER_ERROR_MESSAGE };
  }
}

export const OrgRouter = ({ orgs, onOrgChange, signedUserData, axleDataForNavbar }: OrgAndStaffRouterProps) => {
  useReportingUserIdentify({ forStaff: false });
  const location = useLocation();
  const isUnityMessagingPage = /\/messaging-unity(\/|$)/.test(location.pathname);
  const user = useUser();
  const logout = useLogout();
  const orgUserDetails = getOrgUserDetails(user);
  const { orgId, orgName } = orgUserDetails;
  const { data: userData } = useMyUserQuery();
  const { isCommercial, isTactical } = useEnvironmentType();
  const { isAppSwitcherEnabled, isTeamworksUserManagementEnabled, isUnityMessagingEnabled, isAmsDashboardEnabled } = useNutritionFlags();
  let { userName } = orgUserDetails;
  // SSO login sends user ID as username which we do not want to expose.
  // SSO users also do not have a username, but email is the fallback.
  if (!userName || validate(userName)) {
    const _username = userData?.myUser.username;
    userName = _username ?? undefined;
  }
  const { data: agreementFormData } = useUnsubmittedAgreementFormForUserQuery({ variables: { orgId: orgId || "" }, skip: orgId === "" });
  const [agreementForm, setAgreementForm] = useState<AgreementFormFragment | null>(null);
  const { data: featureFlagsData } = useMyFeatureFlagsQuery();
  const featureFlagsDetails = getFeatureFlagsDetails(featureFlagsData);

  useEffect(() => {
    if (agreementFormData?.unsubmittedAgreementFormForUser) {
      setAgreementForm(agreementFormData.unsubmittedAgreementFormForUser);
    }
  }, [agreementFormData]);

  const [addAgreementFormSubmission] = useAddAgreementFormSubmissionMutation();
  const handleAddAgreementFormSubmission = (agreementFormId: string) => {
    addAgreementFormSubmission({ variables: { input: { agreementFormId } } });
    setAgreementForm(null);
  };

  usePublishedNotificationSubscription({
    onSubscriptionData: ({ subscriptionData, client: { cache } }) => {
      if (subscriptionData.data) {
        const {
          payload: { notification },
        } = subscriptionData.data.notification;
        switch (notification.__typename) {
          case "FoodLogMealCommentNotification":
          case "FoodLogMealEditNotification":
          case "FoodLogMealImageNotification":
          case "MenuOrderCommentNotification":
          case "MenuOrderImageNotification":
          case "TimelineMealCommentNotification":
          case "TimelineMealEditNotification":
          case "TimelineMealImageNotification": {
            if (notification.timelineMeal) {
              addTimelineMealNotificationToCache({ cache, timelineMeal: notification.timelineMeal, notification });
            }
          }
        }
      } else if (subscriptionData.error) {
        console.error("Notification Sub Error!", subscriptionData.error);
      }
    },
  });

  const { profile, navbarImageUrl, appAccess } = axleDataForNavbar ?? {};
  const { axleAppsForAppSwitcher, hasAMS, hasHub } = parseAxleAppAccess(appAccess);
  const orgAvatar = navbarImageUrl ?? undefined;
  const profileAvatar = profile?.imageUrl ?? undefined;
  const profileFullname = `${profile?.givenName} ${profile?.familyName}`;
  const profileName = profile?.isNicknamePreferred
    ? profile.nickname || profile.preferredName || profileFullname
    : profileFullname;

  const handleOrgChange = (orgId: string) => {
    onOrgChange(orgId);
  };
  const { setMessage } = useSnackbar();
  const apolloClient = useApolloClient();

  const handleAppChange = async (app: string, url: string) => {
    const { token, errorMessage } = await getAppSwitcherToken(apolloClient);
    if (token) {
      window.location.href = `${url}?token=${token}`;
    } else {
      setMessage("error", errorMessage);
    }
  };
  const handleUserManagementClick = async () => {
    const hubUrl = axleAppsForAppSwitcher?.hub;
    if (!hubUrl) return;
    const { token, errorMessage } = await getAppSwitcherToken(apolloClient);
    if (token) {
      window.location.href = `${hubUrl}?token=${token}&redirect=/profiles/me&redirect_with_token=true`;
    } else {
      setMessage("error", errorMessage);
    }
  };

  const logo = <NutritionWhiteIcon sx={{ alignSelf: "center", width: 40, height: 40 }} viewBox="0 0 20 20" id="nutrition-logo" />;
  return (
    <Box sx={{ flex: 1, display: "flex" }}>
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          left: -9999,
          zIndex: theme => theme.zIndex.tooltip + 1,
          p: 1,
          backgroundColor: "background.paper",
          "&:focus": { left: 8, top: 8 },
        }}
      >
        Skip to content
      </Box>
      <PrimaryNav
        links={getOrgPrimaryNavLinks(
          orgUserDetails,
          featureFlagsDetails,
          { isUnityMessagingEnabled, isAmsDashboardEnabled },
          { hasAMS, hasHub },
          { isCommercial, isTactical }
        )}
        bottomLinks={
          hasHub && isTeamworksUserManagementEnabled ? getOrgBottomLinks({
            userManagementHandler: handleUserManagementClick
          }) : undefined
        }
        app="nutrition"
        logo={logo}
        team={{
          id: orgId || DEFAULT_ORG_ID,
          name: orgName,
          avatar: orgAvatar,
        }}
        teams={orgs}
        user={
          isTeamworksUserManagementEnabled && profile !== undefined
            ? {
              name: profileName,
              avatar: profileAvatar,
            }
            : {
              name: userName || DEFAULT_USER_NAME,
            }
        }
        additionalSettingsLinks={orgAdditionalSettingsLinks}
        onTeamChange={handleOrgChange}
        onLogout={logout}
        hasHelpCenter={isCommercial}
        hasLearningPortal={isCommercial}
        appSpecificHelpSubmenu={{
          links: isCommercial ? helperMenuLinks : tacticalHelperMenuLinks,
          hideDivider: true,
        }}
        hasMavenSupportChat={featureFlagsDetails.hasMavenChat}
        {...(featureFlagsDetails.hasMavenChat ? { mavenTags: ["nutrition"], mavenSignedUserData: signedUserData } : {})}
        hasAppSwitcher={isAppSwitcherEnabled || isTeamworksUserManagementEnabled}
        apps={axleAppsForAppSwitcher}
        onAppChange={handleAppChange}
      />
      <Box sx={{ flex: 1, overflowY: "hidden", backgroundColor: "black", pt: 1 }}>
        <Box
          id="main-content"
          component="main"
          tabIndex={-1}
          sx={{
            flex: 1,
            height: "100%",
            overflow: "hidden",
            backgroundColor: ({ palette: { mode } }) => (mode === "light" ? "white" : "black"),
            borderTopLeftRadius: 12,
            p: isUnityMessagingPage ? 0 : 3,
          }}
        >
          {isTactical && <InactivityWarning logout={logout} />}
          <Routes>
            <Route element={<AuthRoute />}>
              <Route path="/*" index element={<TeamManagementRouter />} />
              {hasAMS && isAmsDashboardEnabled && (
                <Route path="ams-dashboard/*" element={<AMSDashboardMFE category="nutrition_dashboard" locale="en" />} />
              )}
              <Route path="team-management/*" element={<TeamManagementRouter />} />
              <Route path="kitchen/*" element={<KitchenRouter />} />
              <Route path="menu-item/*" element={<MenuItemRouter />} />
              <Route path="food-management/*" element={<FoodManagementRouter />} />
              <Route path="recipe/*" element={<RecipeRouter />} />
              <Route path="meal-plans/*" element={<MealPlansRouter />} />
              {isCommercial && <Route path="messaging/*" element={<MessagePage />} />}
              {hasHub && isUnityMessagingEnabled && (
                <Route path="messaging-unity/*" element={<UnityMessagingMFE basename="/org/messaging-unity" />} />
              )}
              <Route path="data/*" element={<DataRouter />} />
              <Route path="teams/:teamId/*" element={<TeamRouter />} />
              <Route path="athletes/:athleteId/*" element={<AthleteLegacyRouter />} />
              <Route path="menu-builder/:plannedMenuId/*" element={<MenuBuilderRouter />} />
              <Route path="menu-attendance/:mealMenuId/*" element={<MenuAttendanceRouter />} />
              <Route path="view-attendance/*" element={<ViewAttendanceRouter />} />
              <Route path="settings/*" element={<SettingsRouter />} />
              <Route path="dev/*" element={<DevRouter org={true} />} />
              <Route path="resources/*" element={<EducationResourcesRouter />} />
            </Route>
          </Routes>
        </Box>
      </Box>
      {agreementForm && (
        <AgreementFormModal
          open={!!agreementForm}
          agreementForm={agreementForm}
          onAddAggrementFormSubmission={() => handleAddAgreementFormSubmission(agreementForm.id)}
        />
      )}
    </Box>
  );
};
